import type {RegisteredRuleSchemaComponent} from '../../types/rules-types'
import type {SelectPanelItemInput as ItemInput} from '@primer/react'
import {TrashIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Blankslate} from '@primer/react/experimental'
import {Button, IconButton, FormControl, Link, SelectPanel} from '@primer/react'
import {useMemo, useState} from 'react'

import styles from './CodeScanningTools.module.css'
import {clsx} from 'clsx'

type Value = {
  [KEY in 'name']: string
}

type ToolOption = {
  name: string
  version: string
  description: string
}

const TOOL_PMD: ToolOption = {
  name: 'PMD',
  version: 'v7.18.0',
  description: 'Standard rules',
}

const TOOL_CODEQL: ToolOption = {
  name: 'CodeQL',
  version: 'v2.23.3',
  description: 'Standard queries',
}

const TOOL_ESLINT: ToolOption = {
  name: 'ESLint',
  version: 'v9.39.1',
  description: 'Standard rules',
}

export function CopilotCodeReviewAnalysisTools(
  props: Omit<RegisteredRuleSchemaComponent, 'onValueChange'> & {onValueChange?: (value: Value[]) => void},
) {
  const {field, value, onValueChange, readOnly} = props
  const object = (value || field.default_value) as Value[]
  const selectableTools = useMemo(() => [TOOL_CODEQL, TOOL_ESLINT, TOOL_PMD], [])

  const docUrl = 'https://docs.github.com/copilot/concepts/agents/code-review#about-static-analysis-tools'

  const selectedTools = useMemo(() => {
    const toolNames = object.map(i => i.name)
    return selectableTools.filter(opt => toolNames.includes(opt.name))
  }, [object, selectableTools])

  if (field.type !== 'array' || field.content_object === undefined) {
    return <></>
  }
  return (
    <FormControl>
      <FormControl.Label visuallyHidden>{field.display_name}</FormControl.Label>
      <div className="Box width-full">
        <div className="Box-header">
          <div className="Box-title d-flex flex-justify-between flex-items-center">
            <span className="flex-1">Static analysis tools</span>
            {!readOnly && (
              <ToolPicker
                options={selectableTools}
                value={selectedTools.map(tool => tool.name)}
                onValueChange={newValue =>
                  onValueChange?.(
                    newValue.map(tool => ({
                      name: tool,
                    })),
                  )
                }
              />
            )}
          </div>
        </div>
        {selectedTools.length === 0 ? (
          <Blankslate>
            <Blankslate.Heading as="h2">No static analysis tools selected</Blankslate.Heading>
            {!readOnly && (
              <Blankslate.Description>
                <Link target="_blank" href={docUrl}>
                  Learn more about static analysis tools for Copilot code review
                </Link>
              </Blankslate.Description>
            )}
          </Blankslate>
        ) : (
          <ul>
            {selectedTools.map(tool => (
              <li
                key={tool.name}
                data-testid={`li-${tool.name}`}
                className={clsx(
                  'Box-row d-flex flex-justify-between flex-items-baseline width-full flex-wrap',
                  styles.ToolContainer,
                )}
              >
                <div className="d-flex flex-1 flex-items-center">
                  <span>{tool.name}</span>
                  <span className="fgColor-muted mx-2">{tool.description}</span>
                </div>
                {!readOnly && (
                  <IconButton
                    className="d-inline-block"
                    data-testid="delete-tool-button"
                    icon={TrashIcon}
                    type="button"
                    aria-label={`Delete Tool`}
                    size="small"
                    variant="invisible"
                    onClick={() => onValueChange?.(object.filter(item => item.name !== tool.name))}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </FormControl>
  )
}

function ToolPicker({
  options,
  value,
  onValueChange,
}: {
  options: ToolOption[]
  value: string[]
  onValueChange?: (value: string[]) => void
}) {
  const [text, setText] = useState('')

  const items = options.filter(item => item.name.toLowerCase().startsWith(text.toLowerCase()))

  const itemInputs: ItemInput[] = items.map(item => {
    return {
      text: item.name,
      id: item.name,
      description: `${item.description}`,
      descriptionVariant: 'block',
    }
  })

  const omittedButSelected: ItemInput[] = value
    .filter(item => !items.find(i => i.name === item))
    .map(item => {
      return {text: item, id: item}
    })

  const selected = itemInputs.filter(item => value.includes(item.id as string))
  const [isOpen, setIsOpen] = useState(false)
  const [initialSelections, setInitialSelections] = useState<ItemInput[]>([])

  const onOpenChange = async (state: boolean) => {
    if (state) {
      setInitialSelections(selected)
    } else {
      setInitialSelections([])
    }
    setIsOpen(state)
    if (!state) setText('')
  }

  const onCancel = async () => {
    onValueChange?.(initialSelections.map(v => v.text).sort() as string[])
    onOpenChange(false)
  }

  return (
    <SelectPanel
      title="Select tools"
      renderAnchor={({...props}) => (
        <Button trailingVisual={TriangleDownIcon} aria-haspopup="dialog" {...props}>
          Select tools
        </Button>
      )}
      placeholderText="Search tools"
      open={isOpen}
      onOpenChange={onOpenChange}
      onCancel={onCancel}
      items={itemInputs}
      selected={selected}
      onSelectedChange={(item: ItemInput[]) => {
        if (Array.isArray(item)) {
          const all = [...item, ...omittedButSelected]
          onValueChange?.(all.map(v => v.text).sort() as string[])
        }
      }}
      filterValue={text}
      onFilterChange={setText}
      overlayProps={{
        width: 'medium',
        height: 'small',
      }}
    />
  )
}

try{ CopilotCodeReviewAnalysisTools.displayName ||= 'CopilotCodeReviewAnalysisTools' } catch {}
try{ ToolPicker.displayName ||= 'ToolPicker' } catch {}